/**
 * Job Progress Modal
 * Модальное окно с детальным прогрессом процесса и FLIP анимацией перехода в виджет
 */

import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { BackgroundJob, JobStep, useBackgroundJobsStore } from '../stores/backgroundJobsStore';
import { useModalStore } from '../stores/modalStore';
import { jobsAPI } from '../lib/api';
import { X, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { jobOutcome, progressCaption, readableJobFacts } from './jobProgress.logic';


const JOB_POLL_INTERVAL_MS = 2500;

interface JobProgressModalProps {
  job: BackgroundJob | null;
  isOpen: boolean;
  onClose: () => void;
  widgetElementRef?: React.RefObject<HTMLDivElement>;
}

export default function JobProgressModal({
  job: initialJob,
  isOpen,
  onClose,
  widgetElementRef,
}: JobProgressModalProps) {
  const { showErrorModal, showConfirmModal } = useModalStore()
  const [isAnimating, setIsAnimating] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const { removeJob, cancelJob } = useBackgroundJobsStore();
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Получаем актуальную версию job из store для обновлений в реальном времени
  // Используем useShallow для правильной подписки на изменения в Map
  const job = useBackgroundJobsStore(
    useShallow((state) => {
      if (!initialJob) return null;
      const currentJob = state.jobs.get(initialJob.jobId);
      const result = currentJob || initialJob;
      
      // Логирование для отладки - всегда логируем, чтобы видеть обновления
      console.log('[JobProgressModal] Job selector called:', {
        jobId: initialJob.jobId,
        hasCurrentJob: !!currentJob,
        progress: result.progress,
        status: result.status,
        currentStep: result.currentStep,
      });
      
      // Возвращаем job напрямую - useShallow правильно обработает изменения
      return result;
    })
  );

  // Логирование для отладки - отслеживаем изменения job
  useEffect(() => {
    if (job) {
      console.log('[JobProgressModal] Job updated in component:', {
        jobId: job.jobId,
        progress: job.progress,
        status: job.status,
        currentStep: job.currentStep,
        stepsCount: job.steps.length,
      });
    }
  }, [job?.jobId, job?.progress?.current, job?.progress?.total, job?.status, job?.currentStep]);

  // Опрос статуса job по таймеру (fallback, если WebSocket не доставляет обновления)
  useEffect(() => {
    if (!isOpen || !job || !(job.status === 'processing' || job.status === 'pending')) return;
    const jobId = job.jobId;
    const poll = async () => {
      try {
        const res = await jobsAPI.getJob(jobId);
        const raw = res.data?.data ?? res.data;
        console.log('[POLL DEBUG] getJob response:', { jobId, hasRaw: !!raw, rawJobId: raw?.jobId, rawStatus: raw?.status, rawProgress: raw?.progress, rawSteps: raw?.steps?.length });
        if (!raw || !raw.jobId) {
          console.warn('[POLL DEBUG] No valid job data from API, skipping update');
          return;
        }
        const { updateJob } = useBackgroundJobsStore.getState();
        const progress = raw.progress || {};
        console.log('[POLL DEBUG] Updating job with:', { status: raw.status, progress, stepsCount: raw.steps?.length });
        updateJob(jobId, {
          status: raw.status,
          currentStep: raw.currentStep ?? job.currentStep,
          progress: {
            current: progress.current ?? 0,
            total: progress.total ?? 0,
            percentage: progress.percentage ?? (progress.total > 0 ? Math.round(((progress.current ?? 0) / progress.total) * 100) : 0),
          },
          steps: Array.isArray(raw.steps) ? raw.steps : job.steps,
          completedAt: raw.completedAt,
          error: raw.error,
          metadata: raw.metadata,
        });
      } catch (err) {
        console.error('[POLL DEBUG] Poll error:', err);
      }
    };
    poll();
    const interval = setInterval(poll, JOB_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isOpen, job?.jobId, job?.status]);

  // Автозакрытие при завершении задачи. Задачу с замечанием (например визуалы
  // не выгрузились на FTP) не закрываем: замечание нужно прочитать, а через
  // 3 секунды окно уносило его вместе с собой.
  useEffect(() => {
    if (!job || !isOpen) return;
    if (job.metadata?.pipeline_mode) return;
    if (jobOutcome(job) === 'warning') return;
    if (job.status === 'completed' || job.status === 'failed') {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [job?.status, job?.error, job?.steps, isOpen, onClose]);

  // FLIP анимация при закрытии
  useEffect(() => {
    if (!isOpen && job && modalRef.current && widgetElementRef?.current) {
      setIsAnimating(true);
      
      const widget = widgetElementRef.current;
      const progressBar = progressBarRef.current;
      
      if (progressBar && modalRef.current) {
        // Получаем начальную позицию (прогресс-бар в модальном окне)
        const progressBarRect = progressBar.getBoundingClientRect();
        
        // Получаем конечную позицию (виджет в header)
        const widgetRect = widget.getBoundingClientRect();
        
        // Создаем клон прогресс-бара для анимации
        const clone = progressBar.cloneNode(true) as HTMLElement;
        clone.style.position = 'fixed';
        clone.style.left = `${progressBarRect.left}px`;
        clone.style.top = `${progressBarRect.top}px`;
        clone.style.width = `${progressBarRect.width}px`;
        clone.style.height = `${progressBarRect.height}px`;
        clone.style.zIndex = '9999';
        clone.style.pointerEvents = 'none';
        clone.style.transition = 'none'; // Отключаем переход для первого кадра
        document.body.appendChild(clone);
        
        // Анимируем перемещение
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            clone.style.transition = 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
            clone.style.left = `${widgetRect.left}px`;
            clone.style.top = `${widgetRect.top}px`;
            clone.style.width = `${widgetRect.width}px`;
            clone.style.height = `${widgetRect.height}px`;
            clone.style.opacity = '0';
            
            setTimeout(() => {
              clone.remove();
              setIsAnimating(false);
            }, 500);
          });
        });
      } else {
        setIsAnimating(false);
      }
    } else if (isOpen) {
      setIsAnimating(false);
    }
  }, [isOpen, job, widgetElementRef]);

  if (!isOpen || !job) {
    return null;
  }

  // Итог задачи, а не её статус: прогон с несостоявшейся выгрузкой на FTP
  // формально completed, но зелёной галочки не заслуживает.
  const outcome = jobOutcome(job);
  const pipelineStages = Array.isArray(job.metadata?.pipeline_stages)
    ? job.metadata.pipeline_stages as Array<{
        key?: string;
        label: string;
        current: number;
        total: number;
        determinate?: boolean;
        failed?: number;
        skipped?: number;
        status: JobStep['status'];
      }>
    : [];

  const getJobIcon = (jobOutcomeValue: ReturnType<typeof jobOutcome>) => {
    switch (jobOutcomeValue) {
      case 'success':
        return <CheckCircle className="h-8 w-8 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-8 w-8 text-amber-500" />;
      case 'failed':
        return <XCircle className="h-8 w-8 text-red-500" />;
      default:
        return <Clock className="h-8 w-8 text-blue-500 animate-spin" />;
    }
  };

  const getStepIcon = (status: JobStep['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'processing':
        return <Clock className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-gray-300 dark:border-zinc-700" />;
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center"
      onClick={() => {
        // Если задача stuck - показываем диалог подтверждения
        if (job?.isStuck) {
          setShowConfirmDialog(true);
        } else {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className={`relative bg-white dark:bg-zinc-900 rounded-lg shadow-xl w-full max-w-2xl mx-4 ${
          isAnimating ? 'opacity-0' : 'opacity-100'
        } transition-opacity duration-300`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getJobIcon(outcome)}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">{job.name}</h3>
              <p className="text-sm text-gray-600 dark:text-zinc-300">
                {outcome === 'warning' ? 'Выполнено с замечаниями' : job.currentStep}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              // Если задача stuck - показываем диалог подтверждения
              if (job?.isStuck) {
                setShowConfirmDialog(true);
              } else {
                onClose();
              }
            }}
            className="text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Общий прогресс */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-zinc-200">Общий прогресс</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
                {job.progress && job.progress.total > 0 ? `${job.progress.percentage ?? 0}%` : ''}
              </span>
            </div>
            <div
              ref={progressBarRef}
              className="w-full bg-gray-200 dark:bg-zinc-700 rounded-full h-3 overflow-hidden"
            >
              {job.progress && job.progress.total > 0 ? (
                <div
                  className={`h-full transition-all duration-500 ${
                    outcome === 'success'
                      ? 'bg-green-500'
                      : outcome === 'warning'
                      ? 'bg-amber-500'
                      : outcome === 'failed'
                      ? 'bg-red-500'
                      : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(100, job.progress.percentage ?? 0)}%` }}
                />
              ) : (
                <div className="h-full bg-blue-500 animate-pulse" style={{ width: '100%' }} />
              )}
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-zinc-400">
              <span>{progressCaption(job.progress)}</span>
              <span>
                {job.startedAt ? new Date(job.startedAt).toLocaleTimeString() : '—'} -{' '}
                {job.completedAt
                  ? new Date(job.completedAt).toLocaleTimeString()
                  : 'в процессе...'}
              </span>
            </div>
          </div>

          {pipelineStages.length > 0 && (
            <div className="mb-6 grid gap-3 sm:grid-cols-2">
              {pipelineStages.map((stage) => (
                <div
                  key={stage.key || stage.label}
                  className={`rounded-lg border p-3 ${
                    stage.status === 'completed'
                      ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30'
                      : stage.status === 'processing'
                        ? 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30'
                        : stage.status === 'failed'
                          ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30'
                          : 'border-gray-200 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-900'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-gray-900 dark:text-zinc-100">{stage.label}</span>
                    {getStepIcon(stage.status)}
                  </div>
                  <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-zinc-100">
                    {stage.determinate === false
                      ? <span className="text-sm font-normal text-gray-500 dark:text-zinc-400">Определяем количество…</span>
                      : <>{stage.current} <span className="text-sm font-normal text-gray-500 dark:text-zinc-400">из {stage.total}</span></>}
                  </p>
                  {Boolean(stage.failed) && <p className="mt-1 text-xs text-red-600 dark:text-red-400">Ошибок: {stage.failed}</p>}
                  {Boolean(stage.skipped) && <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Пропущено: {stage.skipped}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Шаги процесса */}
          {pipelineStages.length === 0 && job.steps.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100 mb-3">Шаги процесса</h4>
              <div className="space-y-3">
                {job.steps.map((step, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      step.status === 'completed'
                        ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-900'
                        : step.status === 'processing'
                        ? 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-900'
                        : step.status === 'failed'
                        ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-900'
                        : 'bg-gray-50 dark:bg-zinc-900 border-gray-200 dark:border-zinc-800'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      {getStepIcon(step.status)}
                      <span className="text-sm font-medium text-gray-900 dark:text-zinc-100">{step.name}</span>
                    </div>
                    {/* Полосу показываем только у идущего шага: jobs-service
                        льёт в активный шаг общий прогресс задачи, поэтому у
                        завершённых и не начатых шагов эти числа чужие. */}
                    {step.status === 'processing' && step.progress && step.progress.total > 0 && (
                      <div className="ml-8">
                        <div className="flex items-center justify-end mb-1">
                          <span className="text-xs text-gray-600 dark:text-zinc-300">
                            {Math.min(100, Math.round((step.progress.current / step.progress.total) * 100))}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-zinc-700 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-blue-500 transition-all duration-300"
                            style={{
                              width: `${Math.min(
                                100,
                                (step.progress.current / step.progress.total) * 100
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ошибка либо замечание: задача, доехавшая не до конца (например
              визуалы отрендерены, но не выгружены на FTP), формально завершена
              успешно — красный «Ошибка» был бы неправдой, молчание тоже. */}
          {job.error && (
            outcome === 'warning' ? (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-1">
                  Выполнено с замечаниями:
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-300">{job.error}</p>
              </div>
            ) : (
              <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-900 rounded-lg">
                <p className="text-sm font-medium text-red-900 dark:text-red-200 mb-1">Ошибка:</p>
                <p className="text-sm text-red-700 dark:text-red-300">{job.error}</p>
              </div>
            )
          )}

          {/* Детали задачи: человекочитаемые поля списком, сырой JSON — под спойлером */}
          {job.metadata && Object.keys(job.metadata).length > 0 && (() => {
            const facts = readableJobFacts(job.metadata);
            return (
              <div className="mt-4 rounded-lg border border-gray-200 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-900">
                {facts.length > 0 && (
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-2 p-3 sm:grid-cols-2">
                    {facts.map(([label, value]) => (
                      <div key={label} className="flex items-baseline justify-between gap-3">
                        <dt className="text-xs text-gray-500 dark:text-zinc-400">{label}</dt>
                        <dd className="min-w-0 truncate text-xs font-medium text-gray-900 dark:text-zinc-100" title={value}>
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
                <details className={facts.length > 0 ? 'border-t border-gray-200 dark:border-zinc-800' : ''}>
                  <summary className="cursor-pointer px-3 py-2 text-xs text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200">
                    Технические детали
                  </summary>
                  <pre className="max-h-56 overflow-auto px-3 pb-3 text-xs text-gray-600 dark:text-zinc-300">
                    {JSON.stringify(job.metadata, null, 2)}
                  </pre>
                </details>
              </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-zinc-800 flex justify-end gap-3">
          {/* Кнопка "Прервать" для активных задач */}
          {job && (job.status === 'pending' || job.status === 'processing') && !job.cancelled && !job.metadata?.pipeline_mode && (
            <button
              onClick={async () => {
                if (!job?.jobId) return;

                showConfirmModal(
                  'Вы уверены, что хотите прервать эту задачу?',
                  async () => {
                    setIsCancelling(true);
                    try {
                      await cancelJob(job.jobId);
                      // Закрываем модальное окно после успешной отмены
                      onClose();
                    } catch (error) {
                      console.error('Error cancelling job:', error);
                      showErrorModal('Не удалось прервать задачу. Попробуйте еще раз.');
                    } finally {
                      setIsCancelling(false);
                    }
                  }
                )
              }}
              disabled={isCancelling}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCancelling ? 'Прерывание...' : 'Прервать'}
            </button>
          )}
          
          <button
            onClick={() => {
              // Если задача stuck - показываем диалог подтверждения
              if (job?.isStuck) {
                setShowConfirmDialog(true);
              } else {
                onClose();
              }
            }}
            className="px-4 py-2 bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-200 rounded-md hover:bg-gray-200 dark:hover:bg-zinc-700 transition-colors"
          >
            {(job.status === 'pending' || job.status === 'processing') && job.metadata?.pipeline_mode
              ? 'Свернуть — работа продолжится'
              : 'Закрыть'}
          </button>
        </div>

        {/* Диалог подтверждения для stuck jobs */}
        {showConfirmDialog && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
            onClick={(e) => {
              // Закрываем диалог при клике на фон
              if (e.target === e.currentTarget) {
                setShowConfirmDialog(false);
              }
            }}
          >
            <div 
              className="bg-white dark:bg-zinc-900 rounded-lg p-6 max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4 dark:text-zinc-100">Удалить задачу?</h3>
              <p className="text-gray-600 dark:text-zinc-300 mb-6">
                Эта задача не обновлялась более 5 минут. Вы уверены, что хотите удалить её из списка?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-200 rounded-md hover:bg-gray-200 dark:hover:bg-zinc-700 transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={() => {
                    if (job?.jobId) {
                      removeJob(job.jobId);
                    }
                    setShowConfirmDialog(false);
                    onClose();
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Удалить
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

